/**
 * Launcher-only localStorage state. Device tokens themselves live in lib/tokens.ts
 * (shared with the SDK); everything here is UI memory that nobody else reads.
 */
import type { DeviceTokenEntry } from '../lib/tokens';

const KEYS = {
  lastCircle: 'circles.launcher.lastCircle',
  installed: 'circles.launcher.installedMarked',
  pushEndpoints: 'circles.launcher.pushEndpoints',
  stashed: 'circles.launcher.stashedTokens',
  dismissed: 'circles.launcher.dismissed',
  snoozed: 'circles.launcher.snoozed',
} as const;

function readJson<T extends object>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return parsed && typeof parsed === 'object' ? (parsed as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage full or disabled: UI memory only, safe to drop
  }
}

function updateRecord<V>(key: string, fn: (r: Record<string, V>) => void): void {
  const r = readJson<Record<string, V>>(key, {});
  fn(r);
  writeJson(key, r);
}

/** Tokens are long random strings; a prefix is enough to tell devices apart locally. */
const tokenKey = (token: string) => token.slice(0, 16);

export function getLastCircle(): string | null {
  return localStorage.getItem(KEYS.lastCircle);
}
export function setLastCircle(circleKey: string): void {
  localStorage.setItem(KEYS.lastCircle, circleKey);
}

export function wasMarkedInstalled(token: string): boolean {
  return readJson<Record<string, true>>(KEYS.installed, {})[tokenKey(token)] === true;
}
export function rememberMarkedInstalled(token: string): void {
  updateRecord<true>(KEYS.installed, (r) => {
    r[tokenKey(token)] = true;
  });
}

/** The push endpoint last saved for a device token, so we only re-save when it changes. */
export function savedPushEndpoint(token: string): string | null {
  return readJson<Record<string, string>>(KEYS.pushEndpoints, {})[tokenKey(token)] ?? null;
}
export function rememberPushEndpoint(token: string, endpoint: string): void {
  updateRecord<string>(KEYS.pushEndpoints, (r) => {
    r[tokenKey(token)] = endpoint;
  });
}

/** A real token set aside while an owner previews that circle as someone else. */
export function getStashedToken(circleKey: string): DeviceTokenEntry | null {
  return readJson<Record<string, DeviceTokenEntry>>(KEYS.stashed, {})[circleKey] ?? null;
}
export function stashToken(circleKey: string, entry: DeviceTokenEntry | null): void {
  updateRecord<DeviceTokenEntry>(KEYS.stashed, (r) => {
    if (entry) r[circleKey] = entry;
    else delete r[circleKey];
  });
}

/** Follows a device-token entry that moved from a bare slug to the circle identifier. */
export function renameCircleKey(from: string, to: string): void {
  if (from === to) return;
  const stashed = getStashedToken(from);
  if (stashed) {
    if (!getStashedToken(to)) stashToken(to, stashed);
    stashToken(from, null);
  }
  if (getLastCircle() === from) setLastCircle(to);
}

export function isDismissed(what: string): boolean {
  return readJson<Record<string, true>>(KEYS.dismissed, {})[what] === true;
}
export function dismiss(what: string): void {
  updateRecord<true>(KEYS.dismissed, (r) => {
    r[what] = true;
  });
}

/** Until when (epoch ms) a nudge stays hidden; 0 when it was never snoozed. */
export function snoozedUntil(what: string): number {
  const until = readJson<Record<string, number>>(KEYS.snoozed, {})[what];
  return typeof until === 'number' ? until : 0;
}
export function snooze(what: string, ms: number): void {
  updateRecord<number>(KEYS.snoozed, (r) => {
    r[what] = Date.now() + ms;
  });
}
